import React from "react";
import Shell from "@/pages/LegalShell";

const H2 = ({ children }) => (
  <h2 className="font-heading text-2xl font-bold text-white mt-12 mb-2 tracking-tight">
    {children}
  </h2>
);

const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-slate-300">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

const Terms = () => (
  <Shell
    title="Terms of Service"
    kicker="Plain English. No tricks."
    testid="terms-page"
    path="/terms"
  >
    <p className="text-slate-400">Last updated: 4 June 2026</p>

    <p>
      These Terms of Service (the <strong>Terms</strong>) govern your access to
      and use of <a className="text-cyan-300 hover:text-white" href="https://clydeflow.com">https://clydeflow.com</a>{" "}
      and the ClydeFlow application, products, and services (collectively, the{" "}
      <strong>Service</strong>). The Service is operated by{" "}
      <strong>Paisley on the web</strong>, a company incorporated in the United
      Kingdom with registered number 891394 and registered office at Unit 167603,
      PO Box 7169, Poole, BH15 9EL (<strong>we</strong>, <strong>us</strong>, or{" "}
      <strong>our</strong>).
    </p>
    <p>
      By creating an account, starting a trial, purchasing a plan, or otherwise
      using the Service, you agree to these Terms. If you do not agree, do not use
      the Service.
    </p>

    <H2>1. Eligibility &amp; Accounts</H2>
    <UL
      items={[
        "You must be at least 18 years old and able to enter into a binding contract to use the Service.",
        "You are responsible for the accuracy of the information you provide and for all activity that occurs under your account.",
        "You must keep your login credentials secure and notify us promptly of any unauthorised use.",
      ]}
    />

    <H2>2. The Service</H2>
    <p>
      ClydeFlow is an AI-assisted automation suite for solo-founders and small
      businesses. Features may include content generation, message handling,
      scheduling, and integrations with third-party platforms. We may add, change,
      or remove features at any time to improve or maintain the Service.
    </p>

    <H2>3. Subscriptions, Billing, Trials &amp; Refunds</H2>
    <UL
      items={[
        "Paid plans are billed in advance on a recurring monthly or annual basis through our payment processor, Stripe, until cancelled.",
        "You may start a 14-day no-card trial. The trial includes usage limits (currently 50 DMs and 2 AI blogs); at the end of the trial, access to paid features requires an active subscription.",
        "The Mogul Growth Package is a one-time fee covering a fixed period of Pro access plus done-for-you onboarding, as described at the point of purchase.",
        "You can cancel at any time from your account; cancellation takes effect at the end of the current billing period and you retain access until then.",
        "We offer a full refund if you cancel within 14 days of your first paid charge. Other fees are non-refundable except where required by law.",
        "Prices are shown in GBP and may exclude applicable taxes, which will be added where required.",
      ]}
    />

    <H2>4. Acceptable Use</H2>
    <p>You agree not to use the Service to:</p>
    <UL
      items={[
        "break the law or infringe the rights of others;",
        "send spam, unsolicited messages, or content that is unlawful, harmful, deceptive, harassing, or abusive;",
        "violate the terms, policies, or community guidelines of any connected third-party platform;",
        "attempt to gain unauthorised access to, disrupt, reverse-engineer, or overload the Service or its infrastructure; or",
        "misuse AI features to generate unlawful, infringing, or deceptive content.",
      ]}
    />

    <H2>5. Third-Party Platforms &amp; Integrations</H2>
    <p>
      The Service lets you connect third-party accounts including{" "}
      <strong>TikTok</strong>, <strong>Meta</strong> (Instagram and Facebook),{" "}
      <strong>Google</strong>, and <strong>Stripe</strong>. Your use of those
      platforms through ClydeFlow is also subject to each platform’s own terms and
      policies, including the TikTok Developer Terms, Meta Platform Terms, and
      Google API Services User Data Policy.
    </p>
    <UL
      items={[
        "You are responsible for ensuring your use of connected platforms complies with their terms, and for holding the necessary rights and permissions for any content you publish.",
        "We access only the data permitted by the scopes you grant and use it solely to provide the features you request. See our Privacy Policy for details.",
        "ClydeFlow is an independent product and is not endorsed by, affiliated with, or sponsored by TikTok, Meta, Google, or Stripe.",
        "You can disconnect any platform at any time, which revokes our ongoing access.",
      ]}
    />

    <H2>6. Your Content</H2>
    <p>
      You retain ownership of the content and data you submit to the Service
      (<strong>Your Content</strong>). You grant us a limited, non-exclusive
      licence to host, process, and transmit Your Content only as needed to
      operate and provide the Service to you (including publishing it to platforms
      you direct). You are responsible for Your Content and for having the rights
      to use it.
    </p>

    <H2>7. Intellectual Property</H2>
    <p>
      The Service, including its software, design, branding, and content (other
      than Your Content), is owned by us or our licensors and is protected by
      intellectual property laws. We grant you a limited, non-transferable,
      revocable licence to use the Service in accordance with these Terms. You may
      not copy, resell, or create derivative works from the Service without our
      written permission.
    </p>

    <H2>8. AI-Generated Output</H2>
    <p>
      The Service uses artificial intelligence to help generate content. AI output
      may be inaccurate, incomplete, or unsuitable for your purpose. You are
      responsible for reviewing and approving any AI-generated content before you
      publish or rely on it. We do not guarantee that AI output is original,
      non-infringing, or fit for any particular purpose.
    </p>

    <H2>9. Disclaimers</H2>
    <p>
      The Service is provided “as is” and “as available” without warranties of any
      kind, whether express or implied, to the fullest extent permitted by law.
      We do not warrant that the Service will be uninterrupted, error-free, or
      secure, and we make no guarantee of any specific revenue, growth, or
      results.
    </p>

    <H2>10. Limitation of Liability</H2>
    <p>
      To the fullest extent permitted by law, we will not be liable for any
      indirect, incidental, special, consequential, or punitive damages, or for
      any loss of profits, revenue, data, or goodwill. Our total aggregate
      liability arising out of or relating to the Service will not exceed the
      greater of (a) the amount you paid us in the 12 months before the event
      giving rise to the claim, or (b) £100. Nothing in these Terms excludes
      liability that cannot be excluded under applicable law.
    </p>

    <H2>11. Indemnity</H2>
    <p>
      You agree to indemnify and hold us harmless from any claims, losses, or
      expenses (including reasonable legal fees) arising from Your Content, your
      use of the Service, or your breach of these Terms or of any third-party
      platform’s terms.
    </p>

    <H2>12. Suspension &amp; Termination</H2>
    <p>
      We may suspend or terminate your access to the Service if you breach these
      Terms, create risk or legal exposure for us, or fail to pay applicable fees.
      You may stop using the Service and close your account at any time. On
      termination, your right to use the Service ends; provisions that by their
      nature should survive will continue to apply.
    </p>

    <H2>13. Changes to the Service or Terms</H2>
    <p>
      We may update these Terms from time to time. Material changes will be posted
      on this page and, where appropriate, notified to you. Your continued use of
      the Service after changes take effect constitutes acceptance of the updated
      Terms.
    </p>

    <H2>14. Governing Law &amp; Jurisdiction</H2>
    <p>
      These Terms are governed by the laws of England and Wales, and the courts of
      England and Wales have exclusive jurisdiction, except where mandatory
      consumer protection laws of your country of residence apply.
    </p>

    <H2>15. Contact</H2>
    <div className="text-slate-300 leading-relaxed">
      Paisley on the web (Brian McGuire)
      <br />
      Unit 167603, PO Box 7169, Poole, BH15 9EL
      <br />
      Email:{" "}
      <a className="text-cyan-300 hover:text-white" href="mailto:privacy@clydeflow.com">
        privacy@clydeflow.com
      </a>
    </div>
  </Shell>
);

export default Terms;
